// START GENAI@GHCOPILOT
/* START GENAI@GHCOPILOT */
import { Component, Event, EventEmitter, Fragment, h, Listen, Prop, State, Watch } from '@stencil/core';
import { Condition, Field } from './vs-form-builder.model';
import { getAlignLeftCSS, getDataFromPropString, isDirRtl } from '@utils/utils';
import { getFieldValidators, getInputRenderCss } from './helpers/validator.helper';
import { InputAttr } from './components/input/vs-input.model';
import { decodeHTMLEntities } from '@src/components/vs-form-builder/helpers/rules.helper';
import {
  CONFIRMATION_FIELD,
  FORM_COMPLETE,
  FORM_LOAD,
  REQUIRED_TEXT,
} from '@src/components/vs-form-builder/helpers/constant';
import {
  CityClubList,
  CountryCityList,
  isSkipCaptcha,
} from '@src/workflows/vs-standard-form/vs-standard-form.constant';
import { disableSubmitButton } from '@src/workflows/vs-standard-form/helpers/form.helper';
import { SENTOSA_DATA_KEY_TYPE } from '@src/workflows/vs-standard-form/helpers/auth.helper';

@Component({
  tag: 'vs-form-builder',
  styleUrls: {
    default: './vs-form-builder.scss',
    custom: './vs-form-builder-custom.scss',
  },
  shadow: false,
})
export class VsFormBuilder {

  private formElement: HTMLFormElement;

  /**
   * If true, the form will automatically submit upon certain conditions.
   * @controlName autoSubmit
   */
  @Prop() autoSubmit: boolean;
  /**
   @shortDescription The fields to display in the form.
   @controlName fields
   */
  @Prop() fields: string;

  /* START GENAI@GHCOPILOT */
  @Watch('fields')
  onFieldsPropChange(newValue: string) {
    this.isShowForm = false;
    this.parsedFields = [...this.decodeFields(getDataFromPropString(newValue))];
    const fieldPropRenderingTimeOut = setTimeout(() => {
      clearTimeout(fieldPropRenderingTimeOut);
      this.isShowForm = true;
    }, 10);
  }

  /* END GENAI@GHCOPILOT */

  /**
   @shortDescription If true, align the form fields to the right.
   @controlName right
   */
  @Prop() right: boolean = isDirRtl();

  /**
   @shortDescription The heading to display above the form.
   @controlName heading
   */
  @Prop() heading: string;

  /**
   @shortDescription The description to display above or below the form.
   @controlName description
   */
  @Prop() description: string;

  /**
   @shortDescription The text to display on the submit button.
   @controlName submitButtonText
   */
  @Prop() submitButtonText = 'Submit';

  /**
   @shortDescription The text to display on the reset button.
   @controlName resetButtonText
   */
  @Prop() resetButtonText;

  /**
   @shortDescription The index of the form (if there are multiple forms on the same page).
   @controlName index
   */
  @Prop() index: string = '0';

  /**
   * @shortDescription Flag to set eyebrow label
   * @controlName eyebrow */
  @Prop() eyebrow: boolean;

  /**
   * @shortDescription image url next to the form
   * @controlName sideImgUrl */
  @Prop() sideImgUrl: string;

  /**
   * @shortDescription image alt text next to the form
   * @controlName sideImgAltText */
  @Prop() sideImgAltText: string;

  /**
   * @shortDescription general config for the form to adopt two columns per row
   * @controlName isTwoColumnsPerRow */
  @Prop() isTwoColumnsPerRow = false;

  @Prop() isDefaultShowForm = false;

  /**
   * @shortDescription CSS class for the grid
   * @controlName formGrid */
  @Prop() formGrid: string;

  @Prop() captchaSiteKey!: string;

  /* START GENAI@GHCOPILOT */
  /**
   * @shortDescription Flag to show first four card number
   * @controlName isShowFirstFourCardNumber
   */
  @Prop() isShowFirstFourCardNumber: boolean = true;

  /**
   @shortDescription required text label
   @controlName requiredText
   */
  @Prop() requiredText: string;
  /* END GENAI@GHCOPILOT */

  /**
   @shortDescription Style mode of the component
   @controlName mode */
  @Prop() mode: string;

  /**
   @shortDescription If form requires submission to backend api
   @controlName requireApiSubmission */
  @Prop() requireApiSubmission: boolean = true;

  /**
   @shortDescription If form is maru multi pan workflow
   @controlName isMaruMultiPan */
  @Prop() isMaruMultiPan: boolean = false;

  /**
   * @shortDescription flag for maru workflow
   * @controlName isMaruWorkflow */
  @Prop() isMaruWorkflow: boolean = false;

  /**
   @shortDescription List of added PANs for maru multi PAN form
   @controlName maruCards */
  @Prop() maruCards: string[];

  /**
   * @shortDescription text for add card button
   * @controlName addCardButtonText */
  @Prop() addCardButtonText: string;


  /**
   * @shortDescription text for remove card button
   * @controlName removeCardButtonText
   */
  @Prop() removeCardButtonText

  /**
   * @shortDescription text for add card instruction
   * @controlName addCardButtonInstruction
   */
  @Prop() addCardButtonInstruction: string = ''

  /**
   * @shortDescription text for submit instruction
   * @controlName submitButtonInstruction
   */
  @Prop() submitButtonInstruction: string = ''
  /**
   * @shortDescription text for add card button
   * @controlName addCardButtonText */
  @Prop() isTwoColumnForm: boolean = false;

  /**
   * @shortDescription align to left
   * @controlName isAlignLeft */
  @Prop() isAlignLeft: boolean = false;

  /**
   @shortDescription country list for dropdown display
   @controlName ctryCityList
   */
  @Prop() ctryCityList: CountryCityList[];

  /**
   @shortDescription city list for dropdown display
   @controlName cityClubList
   */
  @Prop() cityClubList: CityClubList[];

  /* START GENAI@GHCOPILOT */
  /**
   @shortDescription flag to indicate if submission requires a modal
   @controlName submitThroughModal
   */
  @Prop() submitThroughModal: boolean;

  /**
   @shortDescription Title of the modal
   @controlName modalTitle
   */
  @Prop() modalTitle: string;

  /**
   @shortDescription content of the modal
   @controlName modalContent
   */
  @Prop() modalContent: string;

  /**
   @shortDescription cancel button text for the modal
   @controlName modalCancelText
   */
  @Prop() modalCancelText: string;

  /**
   @shortDescription confirm button text for the modal
   @controlName modalConfirmText
   */
  @Prop() modalConfirmText: string;
  /* END GENAI@GHCOPILOT */


  @State() isShowForm: boolean;
  @State() isSystemShowForm: boolean;
  @State() formData: object = {};
  @State() fieldRef? = [];
  @State() parsedFields: Field[] = [];
  /* START GENAI@GHCOPILOT */
  @State() isFileUploading: boolean;
  @State() isAddingCard: boolean = false;
  @State() submitModalId: string;
  /* END GENAI@GHCOPILOT */
  /**
   * @shortDescription Emit form submit event when submit button is clicked
   * @controlName formSubmitEvent */
  @Event({
    eventName: 'formSubmitEvent',
    composed: true,
    cancelable: true,
    bubbles: true,
  }) onFormSubmitHandler: EventEmitter;

  /**
   * @shortDescription Emit add card event when user adds card for maru multi pan workflow
   * @controlName addCardEvent */
  @Event({
    eventName: 'addCardEvent',
    composed: true,
    cancelable: true,
    bubbles: true,
  }) onAddCardHandler: EventEmitter;

  @Event({
    eventName: 'removeCardEvent',
    composed: true,
    cancelable: true,
    bubbles: true,
  }) onRemoveCardHandler: EventEmitter;

  /**
   * @shortDescription Emit form loading event
   * @controlName formLoadingEvent */
  @Event({
    eventName: 'formLoadingEvent',
    composed: true,
    cancelable: true,
    bubbles: true,
  }) onFormLoadingHandler: EventEmitter;

  /**
   * @shortDescription Emit file upload event when file is attached to the file input field
   * @controlName fileUploadEvent */
  @Event({
    eventName: 'fileUploadEvent',
    composed: true,
    cancelable: true,
    bubbles: true,
  }) onFileUploadEventHandler: EventEmitter;

  /**
   * @shortDescription Emit form reset event
   * @controlName resetFormEvent */
  @Event({
    eventName: 'resetFormEvent',
    composed: true,
    cancelable: true,
    bubbles: true,
  }) onResetFormEvent: EventEmitter;

  @Listen('isShowFormEvent', { target: 'body' })
  isShowFormHandler(value) {
    this.isSystemShowForm = value.detail;
    this.isShowForm = value.detail;
  }

  @Listen('formResetEvent', { target: 'body' })
  resetFormHandler() {
    this.resetAllFormFields();
  }

  /* START GENAI@GHCOPILOT */
  @Listen('fieldStrEvent', { target: 'body' })
  isFormStrEvent(value: CustomEvent<string>) {
    this.fields = value.detail;
  }

  /* END GENAI@GHCOPILOT */

  @Listen('uploadFileInProgressEvent', { target: 'body' })
  uploadingInProgress(uploadFileInProgressEvent: { detail: { isUploading: boolean; }; }) {
    this.isFileUploading = uploadFileInProgressEvent?.detail?.isUploading;
  }

  componentWillLoad() {
    if (!this.isSystemShowForm) {
      this.isShowForm = this.isDefaultShowForm;
    }
    this.sendGAFormEvent(FORM_LOAD);
    this.onFieldsPropChange(this.fields);
  }

  componentDidLoad() {
    /* START GENAI@GHCOPILOT */
    // only try to load captcha when sitekey is provided
    if (this.captchaSiteKey) {
      this.loadCaptchaInstances().then();
    } else {
      console.log('sitekey is not provided');
    }
    /* END GENAI@GHCOPILOT */
  }

  async loadCaptchaInstances(retries = 3) {
    const hCaptchaInstances = document.querySelectorAll('vs-hcaptcha');

    if (hCaptchaInstances?.length > 0 && !this.renderedCaptcha) {
      for (let hCaptcha of hCaptchaInstances) {
        hCaptcha?.loadCaptcha();
      }
      this.renderedCaptcha = true;
      console.log('hCaptcha', hCaptchaInstances);
    } else if (retries > 0) {
      // Try again after a delay if there are retries left
      await new Promise(resolve => setTimeout(resolve, 500));  // 1-second delay
      this.loadCaptchaInstances(retries - 1).then();
    } else {
      console.error('Failed to load hCaptcha instances after multiple attempts');
    }
  }


  private formFieldsToSubmit: object = {};

  private renderedCaptcha: boolean = false;

  private sendGAFormEvent = (eventType: String) => {
    const formTrackingEvent = new CustomEvent('form_tracking', {
      detail: {
        componentType: '',
        component: 'form',
        eventAction: eventType,
        siteSection: 'global promotion',
        clickText: '',
        formName: window.document.title,
        formField1: '',
        formField2: '',
      },
    });
    document?.dispatchEvent(formTrackingEvent);
    console.log('form tracking event', formTrackingEvent);
  };

  private decodeFields = (parsedFields: Field[]): Field[] => {
    return parsedFields?.map(parsedField => ({
      ...parsedField,
      requiredText: decodeHTMLEntities(parsedField?.requiredText),
      label: decodeHTMLEntities(parsedField?.label),
      data: parsedField?.data?.map(elementField => ({
        ...elementField,
        value: decodeHTMLEntities(elementField?.value),
      })),
    }));
  };

  private onFieldChange = (e: CustomEvent) => {
    const field = e.detail as InputAttr;
    this.formData = {
      ...this.formData,
      [field.index]: field,
    };
    this.sentosaFieldChangeReRender(field);
  };


  // START GENAI@GHCOPILOT
 /**
   * Re-renders the form fields based on the changes in the triggering field.
   * @param triggeringField - The field that triggered the change.
   */
  private sentosaFieldChangeReRender = (triggeringField: InputAttr) => {
    if (this.cityClubList && this.ctryCityList) {
      if (triggeringField.key === SENTOSA_DATA_KEY_TYPE.country) {
        this.updateDependentFields(triggeringField, [SENTOSA_DATA_KEY_TYPE.city, SENTOSA_DATA_KEY_TYPE.club]);
      } else if (triggeringField.key === SENTOSA_DATA_KEY_TYPE.city) {
        this.updateDependentFields(triggeringField, [SENTOSA_DATA_KEY_TYPE.club]);
      }
    }
  };

  /**
   * Updates the dependent fields based on the triggering field.
   * @param triggeringField - The field that triggered the change.
   * @param dependentKeys - The keys of the dependent fields to update.
   */
  private updateDependentFields = (triggeringField: InputAttr, dependentKeys: string[]) => {
    dependentKeys.forEach(dependentKey => {
      const dependentFieldIndex = this.parsedFields?.findIndex(field => field.name === dependentKey);
      this.fieldRef[dependentFieldIndex]?.resetValue();
      this.refreshOptionsBasedOnDependencies(triggeringField, dependentFieldIndex);
    });
  };

  /**
   * Refreshes the options of the dependent fields based on the triggering field.
   * @param triggeringField - The field that triggered the change.
   * @param dependentFieldIndex - The index of the dependent field.
   */
  private refreshOptionsBasedOnDependencies = (triggeringField: InputAttr,
                                               dependentFieldIndex: number = null) => {
    if (dependentFieldIndex === null) return;

    const dependentField = this.fieldRef[dependentFieldIndex];
    switch (triggeringField?.key) {
      case SENTOSA_DATA_KEY_TYPE.country:
        dependentField.dependsOnValues = [triggeringField?.value];
        break;
      case SENTOSA_DATA_KEY_TYPE.city:
        dependentField.dependsOnValues = [dependentField.dependsOnValues?.[0] || '', triggeringField?.value];
        break;
      default:
        break;
    }
  };
  // END GENAI@GHCOPILOT

  private onFileFieldChange = (e: CustomEvent) => {
    this.onFieldChange(e);
    if (e?.detail?.isValid && e?.detail?.value) {
      this.onFileUploadEventHandler.emit(e?.detail?.value);
    }
  };

  private isFieldValid = (field: any) => {
    return this.formData?.[field]?.isValid;
  };

  private checkIfRequireSubmissionToBackend = () => {
    return this.requireApiSubmission;
  };

  private onFormSubmit = (e: Event) => {
    e.preventDefault();
    const { isValid, toSubmitFormFields, firstInValidIndex }
      = this.validateFormFields(this.getFormDataKeys());
    this.onFormLoadingHandler.emit(true);
    if (!this.checkIfRequireSubmissionToBackend()) {
      /* START GENAI@GHCOPILOT */
      this.onFormSubmitHandler.emit(toSubmitFormFields);
      return;
      /* END GENAI@GHCOPILOT */
    }
    if (!isValid) {
      this.fieldRef[firstInValidIndex].setFocus();
      this.onFormSubmitHandler.emit(toSubmitFormFields);
    } else {
      this.formFieldsToSubmit = this.removeConfirmationFormField(toSubmitFormFields);
      const hCaptcha = document.querySelector('vs-hcaptcha');
      disableSubmitButton(true);
      if (hCaptcha) {

        hCaptcha.executeCaptcha();
      } else {
        this.submitFormToBackend(isSkipCaptcha);
      }
    }
  };

  private removeConfirmationFormField = (formFields) => {
    const finalFormFields = {};
    /* START GENAI@GHCOPILOT */
    for (const key in formFields) {
      if (key.slice(-7) !== CONFIRMATION_FIELD) {
        finalFormFields[key] = formFields[key];
      }
    }
    /* END GENAI@GHCOPILOT */
    return finalFormFields;
  };

  submitFormToBackend = (captchaResponse: string) => {
    this.formFieldsToSubmit['hcaptcha'] = captchaResponse;
    this.sendGAFormEvent(FORM_COMPLETE);
    this.onFormSubmitHandler.emit(this.formFieldsToSubmit);
  };

  addCardInfo = (captchaResponse: string) => {
    this.formFieldsToSubmit['hcaptcha'] = captchaResponse;
    this.isAddingCard = false;
    this.onAddCardHandler.emit(this.formFieldsToSubmit);
  };

  // START GENAI@GHCOPILOT
 removeCardInfo = (card: string) =>{
    this.onRemoveCardHandler.emit(card)
  }
  // END GENAI@GHCOPILOT

  private getFormDataKeys = () => {
    //@ts-ignore
    return Object.keys(this.formData);
  };

  private validateFormFields = (formDataKeys: string[]) => {
    let isValid = true;
    let firstInValidIndex = -1;
    const toSubmitFormFields = {};

    for (const formKey of formDataKeys) {
      const formFieldData = this.formData[formKey];
      toSubmitFormFields[formFieldData.key] =
        {
          value: formFieldData.value,
          error: formFieldData?.isValid ? undefined : formFieldData.error,
        };
      const isFieldValid = this.isFieldValid(formKey);
      if (!isFieldValid) {
        if (firstInValidIndex === -1) {
          firstInValidIndex = this.formData[formKey].index;
        }
        this.fieldRef[formKey].setFocus();
      }
      isValid &&= isFieldValid;
    }

    return { isValid, toSubmitFormFields, firstInValidIndex };
  };

  @Listen('resetVCESFormEvent', { target: 'body' })
  resetVCESFormHandler() {
    this.resetAllFormFields();
  }

  // START GENAI@GHCOPILOT
 @Listen('maruAddCardVerifiedEvent', { target: 'body' })
  addCardVerifiedHandler() {
    console.log("maruAddCardVerified")
    this.resetAllFormFields();
  }
  // END GENAI@GHCOPILOT

// START GENAI@GHCOPILOT
  @Listen('captchaRenderedEvent')
  setHcaptchaRendered() {
    if (this.autoSubmit) {
      this.formElement.requestSubmit();
    }
  }

  private resetFormLoadingState() {
    this.onFormLoadingHandler.emit(false);
    disableSubmitButton(false);
  }

  // END GENAI@GHCOPILOT

  private resetAllFormFields() {
    this.fieldRef.forEach(childRef => {
      childRef.resetValue();
    });
  }

  private resetFormWithEventEmit = () => {
    this.onResetFormEvent.emit(true);
    this.resetAllFormFields();
  };

  getRequiredText = (field: Field) => {
    return field?.requiredText || this.requiredText || REQUIRED_TEXT;
  };

  addCard = (e: Event) => {
    e.preventDefault();
    this.isAddingCard = true;
    this.onFormSubmit(e);
  };

  checkCondition = (condition: Condition) => {
    // check if there's any condition to be met before displaying element
    /* START GENAI@GHCOPILOT */
    if (!condition) return true;

    return Object.values(this.formData).some(data => data.key === condition.key && data.value === condition.value);
    /* END GENAI@GHCOPILOT */
  };

  formSubmitByModal = (evt: Event) => {
    evt.preventDefault();
    this.submitModalId = `submitModal-${Math.random()}`;
    const modalToggleTimeOut = setTimeout(() => {
      clearTimeout(modalToggleTimeOut);
      let openEvent = new CustomEvent('vs_modal_toggle', {
        detail: {
          id: this.submitModalId,
          open: true,
        },
      });
      document.dispatchEvent(openEvent);
    }, 200);
  }

  closeSubmitModal= (e: Event) => {
    e.preventDefault();
    let closeEvent
      = new CustomEvent('vs_modal_toggle', {
      detail: {
        id: this.submitModalId,
        open: false,
      },
    });
    document.dispatchEvent(closeEvent);
    this.submitModalId = null;
  }

  renderSubmitModal = () => {
    return <vs-modal modal-id={this.submitModalId}
                     modal-title={this.modalTitle}
                     modal-content={this.modalContent}
                     trigger-ref='submitBtn'
                     cancelText={this.modalCancelText}
                     confirmText={this.modalConfirmText}
                     onModalCloseEvent={this.closeSubmitModal}
                     onModalConfirmEvent={(evt) => {
                       this.closeSubmitModal(evt);
                       this.onFormSubmit(evt);
                     }}
    />;
  }

  renderForm = () => {
    return (
      <div class={this.isTwoColumnForm && 'vs-col-12 vs-col-sm-8'}>
        <form ref={(el) => (this.formElement = el as HTMLFormElement)}
              novalidate
              class={`promotionFormLL vs-mx-auto vs-form vs-row ${this.formGrid ? this.formGrid : 'vs-col-lg-9'} vs-col-12`}
              onSubmit={this.onFormSubmit}>
          {this.submitModalId && this.renderSubmitModal()}
          {/* Form Error Handling Section */}
          <div class='vs-col-12'>
            <slot name='formFieldErrorSlot' />
          </div>
          {/* END Form Error Handling Section */}
          <div class={this.sideImgUrl ? 'vs-row vs-mx-0 vs-form-group vs-mb-1' : 'vs-col-12 vs-px-0'}>
            {this.sideImgUrl &&
              <img class='vs-col-lg-5 vs-col-md-5 vs-col-12' src={this.sideImgUrl}
                   alt={this.sideImgAltText} />
            }
            <div class={this.sideImgUrl ? 'vs-col-lg-7 vs-col-md-7 vs-col-12' : 'vs-row vs-col-12 vs-px-0 vs-mx-0'}>

              <slot name='preFormTncSlot' />

              {this.parsedFields?.map((field: Field, index: number) => {
                switch (field.type) {
                  case 'cardInput': {
                    return (
                      <vs-card-input ref={el => (this.fieldRef[index] = el)}
                                     class={getInputRenderCss(field?.singleColumn, this.isTwoColumnsPerRow, this.checkCondition(field?.renderIf))}
                                     label={field.label}
                                     index={index}
                                     key={field.name}
                                     inputId={field.name}
                                     tooltip={field?.tooltip}
                                     isShowFirstFourCardNumber={this.isShowFirstFourCardNumber}
                                     requiredText={this.getRequiredText(field)}
                                     aboveInputContent={field?.aboveInputContent}
                                     betweenInputContent={field?.betweenInputContent}
                                     belowInputContent={field?.belowInputContent}
                                     onComputedInputValue={this.onFieldChange}
                                     validators={getFieldValidators(field)}
                      />
                    );
                  }
                  case 'dropdown': {
                    return (
                      <vs-dropdown
                        ref={el => (this.fieldRef[index] = el)}
                        class={getInputRenderCss(field?.singleColumn, this.isTwoColumnsPerRow, this.checkCondition(field?.renderIf))}
                        key={field.name}
                        inputId={field?.name}
                        index={index}
                        eyebrow={this.eyebrow}
                        label={field.label}
                        placeholder={field.placeholder}
                        requiredText={this.getRequiredText(field)}
                        data={field?.data}
                        aboveInputContent={field?.aboveInputContent}
                        betweenInputContent={field?.betweenInputContent}
                        belowInputContent={field?.belowInputContent}
                        onComputedInputValue={this.onFieldChange}
                        validators={getFieldValidators(field)}
                      />);
                  }
                  case 'sentosa-dropdown' : {
                    return (
                      <vs-sentosa-dropdown
                        ref={el => (this.fieldRef[index] = el)}
                        class={getInputRenderCss(field?.singleColumn, this.isTwoColumnsPerRow, this.checkCondition(field?.renderIf))}
                        key={field.name}
                        inputId={field?.name}
                        index={index}
                        eyebrow={this.eyebrow}
                        label={field.label}
                        placeholder={field.placeholder}
                        ctryCityList={this.ctryCityList}
                        cityClubList={this.cityClubList}
                        requiredText={this.getRequiredText(field)}
                        aboveInputContent={field?.aboveInputContent}
                        betweenInputContent={field?.betweenInputContent}
                        belowInputContent={field?.belowInputContent}
                        onComputedInputValue={this.onFieldChange}
                        validators={getFieldValidators(field)}
                      />);
                  }
                  case 'checkbox': {
                    return (
                      <vs-checkbox
                        ref={el => (this.fieldRef[index] = el)}
                        class={getInputRenderCss(field?.singleColumn, this.isTwoColumnsPerRow, this.checkCondition(field?.renderIf))}
                        key={field.name}
                        data={field.data}
                        inputId={field.name}
                        index={index}
                        label={field.label}
                        tooltip={field?.tooltip}
                        requiredText={this.getRequiredText(field)}
                        is-inline={field.isInline}
                        aboveInputContent={field?.aboveInputContent}
                        betweenInputContent={field?.betweenInputContent}
                        belowInputContent={field?.belowInputContent}
                        onComputedInputValue={this.onFieldChange}
                        validators={getFieldValidators(field)}
                      />
                    );
                  }
                  case 'radio': {
                    return (
                      <vs-radio
                        ref={el => (this.fieldRef[index] = el)}
                        data={field.data}
                        label={field.label}
                        inputId={field.name}
                        index={index}
                        requiredText={this.getRequiredText(field)}
                        is-inline={field.isInline}
                        aboveInputContent={field?.aboveInputContent}
                        betweenInputContent={field?.betweenInputContent}
                        belowInputContent={field?.belowInputContent}
                        validators={getFieldValidators(field)}
                        onComputedInputValue={this.onFieldChange}
                        class={getInputRenderCss(field?.singleColumn, this.isTwoColumnsPerRow, this.checkCondition(field?.renderIf))}
                      />
                    );
                  }
                  case 'file': {
                    return (
                      <vs-file-upload ref={el => (this.fieldRef[index] = el)}
                                      class={getInputRenderCss(field?.singleColumn, this.isTwoColumnsPerRow, this.checkCondition(field?.renderIf))}
                                      key={field.name}
                                      inputId={field?.name}
                                      accepts={field.accepts}
                                      index={index}
                                      requiredText={this.getRequiredText(field)}
                                      label={field.label}
                                      tooltip={field?.tooltip}
                                      successUploadMessage={field.successUploadMessage}
                                      uploadErrorMessage={field.uploadErrorMessage}
                                      uploadingMessage={field.uploadingMessage}
                                      fileFormatErrorMessage={field.fileFormatErrorMessage}
                                      fileSizeErrorMessage={field.fileSizeErrorMessage}
                                      aboveInputContent={field?.aboveInputContent}
                                      betweenInputContent={field?.betweenInputContent}
                                      belowInputContent={field?.belowInputContent}
                                      onComputedInputValue={this.onFileFieldChange}
                                      validators={getFieldValidators(field)}
                      />
                    );
                  }
                  case 'date': {
                    return <vs-input ref={el => (this.fieldRef[index] = el)}
                                     class={getInputRenderCss(field?.singleColumn, this.isTwoColumnsPerRow, this.checkCondition(field?.renderIf))}
                                     isTextArea={false}
                                     isDate={true}
                                     type='text'
                                     key={field.name}
                                     inputId={field?.name}
                                     index={index}
                                     eyebrow={this.eyebrow}
                                     label={field.label}
                                     defaultValue={field?.value}
                                     requiredText={this.getRequiredText(field)}
                                     placeholder={field?.placeholder}
                                     aboveInputContent={field?.aboveInputContent}
                                     betweenInputContent={field?.betweenInputContent}
                                     belowInputContent={field?.belowInputContent}
                                     onComputedInputValue={this.onFieldChange}
                                     validators={getFieldValidators(field)}
                    />;
                  }
                  default: {
                    return (
                      <vs-input ref={el => (this.fieldRef[index] = el)}
                                class={getInputRenderCss(field?.singleColumn, this.isTwoColumnsPerRow, this.checkCondition(field?.renderIf))}
                                isTextArea={field.type === 'textarea'}
                                type={field.type}
                                key={field.name}
                                inputId={field?.name}
                                index={index}
                                eyebrow={this.eyebrow}
                                label={field.label}
                                defaultValue={field?.value}
                                requiredText={this.getRequiredText(field)}
                                placeholder={field?.placeholder}
                                aboveInputContent={field?.aboveInputContent}
                                betweenInputContent={field?.betweenInputContent}
                                belowInputContent={field?.belowInputContent}
                                onComputedInputValue={this.onFieldChange}
                                validators={getFieldValidators(field)}
                                inputPrefix={field?.inputPrefix}
                      />
                    );
                  }
                }
              })}
            </div>
          </div>
          {(this.isMaruMultiPan && this.maruCards.length < 5) &&
            <Fragment><button onClick={this.addCard} class='vs-btn vs-btn-primary'>{this.addCardButtonText}</button>
              <div class='vs-col-12 vs-px-0' innerHTML={this.addCardButtonInstruction}></div></Fragment>
          }
          {this.formFieldsToSubmit &&
            <p class='vs-col-12 vs-px-0'>
              <slot name='apiErrorSlot' />
            </p>}
          {this.isMaruMultiPan && this.maruCards.length > 0 && <div class="vs-col-12 vs-px-0"><h2 class="vs-h4">Added Cards</h2></div>}

          {(this.isMaruMultiPan && this.maruCards.length > 0) &&
            this.maruCards.map((pan) => {
              return (
                <div class="vs-col-12 vs-px-0">
                  <div class="vs-row vs-no-gutters vs-justify-content-around">
                    <div class="vs-align-content-center">
                      <p class="vs-text vs-font-weight-bold" >{pan.substring(0, 4)}</p>
                    </div>
                    <div class="vs-align-content-center">
                      <p class="vs-text vs-font-weight-bold" style={{"-webkit-text-security": "disc"}}>
                        ....</p>
                    </div>
                    <div class="vs-align-content-center">
                      <p class="vs-text vs-font-weight-bold" style={{"-webkit-text-security": "disc"}}>
                        ....</p>
                    </div>
                    <div class="vs-align-content-center">
                      <p class="vs-text vs-font-weight-bold">{pan.substring(12, 16)}</p>
                    </div>
                    <div>
                      <button class="vs-btn vs-btn-primary" type="button" onClick={() => this.removeCardInfo(pan)}>{this.removeCardButtonText}
                      </button>
                    </div>
                  </div>
                </div>
              )
            })}
          {(this.isMaruWorkflow) && <div class='vs-col-12 vs-px-0' innerHTML={this.submitButtonInstruction}></div>}
          {(!this.autoSubmit && !this.submitThroughModal) && <button id='submitBtn' type='submit' disabled={this.isFileUploading}
                                         class='vs-btn vs-btn-primary vs-btn-block vs-mr-2'>{this.submitButtonText}</button>}
          {this.submitThroughModal && <button id='submitBtn' onClick={this.formSubmitByModal} disabled={this.isFileUploading}
                                              class='vs-btn vs-btn-primary vs-btn-block vs-mr-2'>{this.submitButtonText}</button>}
          {this.resetButtonText &&
            <button type='button' class='vs-btn vs-btn-block vs-mr-2' disabled={this.isFileUploading}
                    onClick={this.resetFormWithEventEmit}>{this.resetButtonText}</button>}
          {this.captchaSiteKey && <vs-hcaptcha
            onCaptchaVerifiedEvent={(event) => {
              if (this.isAddingCard) {
                this.addCardInfo(event.detail);
              } else {
                this.submitFormToBackend(event.detail);
              }
            }}
            onCaptchaTimeoutEvent={() => {
              this.resetFormLoadingState();
            }}
            onCaptchaExpiredEvent={() => {
              this.resetFormLoadingState();
            }}
            onChallengeClosedEvent={() => {
              this.resetFormLoadingState();
            }}
            onCaptchaErrorEvent={() => {
              this.resetFormLoadingState();
            }}
            sitekey={this.captchaSiteKey} size='invisible' lazy={true} id='1'></vs-hcaptcha>}
        </form>
      </div>
    );
  };

  render() {
    return (
      <div class='vs-container vs-px-2' id={this.index?.toString()}>
        <div class={this.isTwoColumnForm && 'vs-row'}>
          {this.isShowForm && !this.autoSubmit &&
            <div class={this.isTwoColumnForm && 'vs-col-sm-4 vs-col-12 position-relative text-white'}>
              {/* Heading Section */}

              <slot name='headingSlot' />
              {this.heading?.length > 0 &&
                (this.index === '0' ?
                    <h1 class={`vs-h2 vs-mt-3 ${getAlignLeftCSS(this.isAlignLeft)}`}
                        innerHTML={this.heading} /> :
                    <h2 class={`vs-h2 vs-mt-3 ${getAlignLeftCSS(this.isAlignLeft)}`}
                        innerHTML={this.heading} />
                )}

              {this.description?.length > 0 &&
                <p class={`vs-text ${getAlignLeftCSS(this.isAlignLeft)} vs-description`}
                   innerHTML={this.description} />}

              <slot name='descriptionSlot' />
            </div>
          }
          {/* END Heading Section */}
          {/* Form Section */}
          {this.isShowForm ? this.renderForm() :
            <slot name='successSlot' />
          }
          {
            this.isShowForm && <slot name='additionalContentSlot' />
          }
          <slot name='footerSlot' />
        </div>
      </div>
    );
  }
}

/* END GENAI@GHCOPILOT */
// END GENAI@GHCOPILOT
